import React, { useState } from 'react';
import { X, Flag, ChevronRight, CheckCircle } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const REASONS = ['不當內容', '騷擾', '詐騙', '其他'];

// travelPostId (optional): when reporting a travel post, links the report to it
export default function ReportModal({ targetUser, accessToken, reporterId, travelPostId = null, onClose }) {
    const [step, setStep] = useState('select'); // 'select' | 'describe' | 'done'
    const [selectedReason, setSelectedReason] = useState(null);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!selectedReason) return;
        setIsSubmitting(true);
        setError('');
        try {
            const { data: recentReports } = await supabase
                .from('reports')
                .select('id, created_at')
                .eq('reporter_id', reporterId)
                .eq('reported_user_id', targetUser.id)
                .order('created_at', { ascending: false })
                .limit(1);

            if (recentReports && recentReports.length > 0) {
                const lastReport = new Date(recentReports[0].created_at);
                if (Date.now() - lastReport.getTime() < 24 * 60 * 60 * 1000) {
                    setError('你最近已經檢舉過這位用戶，請稍後再試。');
                    setIsSubmitting(false);
                    return;
                }
            }

            const { error: insertError } = await supabase
                .from('reports')
                .insert({
                    reporter_id: reporterId,
                    reported_user_id: targetUser.id,
                    reason: selectedReason,
                    description,
                    // 僅在檢舉貼文時帶欄位，避免 DB migration 尚未套用時影響一般檢舉
                    ...(travelPostId ? { related_travel_post_id: travelPostId } : {})
                });

            if (insertError) {
                console.error('Failed to submit report:', insertError);
                setError('送出失敗，請稍後再試。');
                setIsSubmitting(false);
                return;
            }
            setStep('done');
        } catch {
            setError('網路錯誤，請確認連線後再試。');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Overlay ──────────────────────────────────────────────────
    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, zIndex: 2000,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            }}
        >
            <div style={{
                width: '100%', maxWidth: '480px',
                background: 'white',
                borderRadius: '24px 24px 0 0',
                padding: '0 0 40px',
                boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
                animation: 'slideUp 0.25s ease',
            }}>

                {/* Handle bar */}
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '4px' }}>
                    <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e5e7eb' }} />
                </div>

                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px 12px',
                    borderBottom: '1px solid #f1f5f9',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Flag size={18} color="#ef4444" />
                        <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1a1a1a' }}>
                            {step === 'done' ? '已送出檢舉' : `檢舉 ${targetUser?.profile?.name || '此用戶'}`}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: '#f1f5f9', border: 'none', borderRadius: '50%',
                            width: '32px', height: '32px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <X size={16} color="#6b7280" />
                    </button>
                </div>

                {/* ── Done state ── */}
                {step === 'done' && (
                    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                        <CheckCircle size={56} color="#10b981" style={{ margin: '0 auto 16px' }} />
                        <p style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1a1a1a', marginBottom: '8px' }}>
                            感謝你的回報
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '0.9rem', lineHeight: '1.6' }}>
                            我們會審查這份檢舉，並採取適當的行動來維護社群安全。
                        </p>
                        <button
                            onClick={onClose}
                            style={{
                                marginTop: '28px',
                                width: '100%', padding: '14px',
                                background: '#4e8bca', color: 'white',
                                border: 'none', borderRadius: '12px',
                                fontWeight: '600', fontSize: '1rem', cursor: 'pointer',
                            }}
                        >
                            關閉
                        </button>
                    </div>
                )}

                {/* ── Select reason ── */}
                {step === 'select' && (
                    <div style={{ padding: '16px 20px 0' }}>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '16px' }}>
                            選擇檢舉原因
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {REASONS.map((reason) => (
                                <button
                                    key={reason}
                                    onClick={() => setSelectedReason(reason)}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '14px 16px',
                                        background: selectedReason === reason ? '#eef4f9' : '#f8fafc',
                                        border: `2px solid ${selectedReason === reason ? '#4e8bca' : '#f1f5f9'}`,
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <span style={{
                                        fontWeight: '600', fontSize: '0.95rem',
                                        color: selectedReason === reason ? '#376ba1' : '#374151',
                                    }}>
                                        {reason}
                                    </span>
                                    {selectedReason === reason && <CheckCircle size={18} color="#4e8bca" />}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => { if (selectedReason) setStep('describe'); }}
                            disabled={!selectedReason}
                            style={{
                                marginTop: '20px', width: '100%', padding: '14px',
                                background: selectedReason ? '#4e8bca' : '#e5e7eb',
                                color: selectedReason ? 'white' : '#9ca3af',
                                border: 'none', borderRadius: '12px',
                                fontWeight: '600', fontSize: '1rem',
                                cursor: selectedReason ? 'pointer' : 'default',
                                transition: 'background 0.2s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                            }}
                        >
                            下一步 <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                {/* ── Describe ── */}
                {step === 'describe' && (
                    <div style={{ padding: '16px 20px 0' }}>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '4px' }}>
                            原因：<strong style={{ color: '#374151' }}>{selectedReason}</strong>
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '14px' }}>
                            補充說明（選填）
                        </p>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="請描述你觀察到的問題..."
                            maxLength={500}
                            rows={4}
                            style={{
                                width: '100%', padding: '12px 14px',
                                background: '#f8fafc',
                                border: '1px solid #f1f5f9',
                                borderRadius: '12px',
                                fontSize: '0.9rem', color: '#374151',
                                resize: 'none', outline: 'none',
                                boxSizing: 'border-box',
                                fontFamily: 'inherit',
                            }}
                        />
                        <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
                            {description.length}/500
                        </div>

                        {error && (
                            <div style={{
                                marginTop: '10px', padding: '10px 14px',
                                background: '#fef2f2', border: '1px solid #fecaca',
                                borderRadius: '10px', color: '#ef4444', fontSize: '0.875rem',
                            }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                            <button
                                onClick={() => setStep('select')}
                                style={{
                                    flex: 1, padding: '14px',
                                    background: '#f3f4f6', color: '#374151',
                                    border: 'none', borderRadius: '12px',
                                    fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer',
                                }}
                            >
                                返回
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                style={{
                                    flex: 2, padding: '14px',
                                    background: isSubmitting ? '#9ca3af' : '#ef4444',
                                    color: 'white', border: 'none', borderRadius: '12px',
                                    fontWeight: '600', fontSize: '0.95rem',
                                    cursor: isSubmitting ? 'default' : 'pointer',
                                    transition: 'background 0.2s ease',
                                }}
                            >
                                {isSubmitting ? '送出中...' : '送出檢舉'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
        </div>
    );
}
